import { HttpApiService } from "@/shared/services/httpApi.service";
import { normalizeBlobError, parseContentDispositionFilename } from "@/shared/utils/downloadBlob";

import { WALLET_TOP_UPS_URL } from "../constants/walletTopUps.constants";
import type {
  CreateWalletTopUpPayload,
  ListWalletTopUpsParams,
  WalletTopUp,
  WalletTopUpCreated,
  WalletTopUpReceiptFile,
  WalletTopUpsPage,
} from "../types/walletTopUps.types";

/** `/internal/payments/wallet-top-ups` — the only place that knows the wire. */
export const WalletTopUpsService = {
  async access(): Promise<boolean> {
    const { data } = await HttpApiService.get<{ allowed: boolean }>(`${WALLET_TOP_UPS_URL}/access`);
    return data.allowed === true;
  },

  async list(params: ListWalletTopUpsParams): Promise<WalletTopUpsPage> {
    const { data } = await HttpApiService.get<WalletTopUpsPage>(WALLET_TOP_UPS_URL, { params });
    return data;
  },

  async get(id: string): Promise<WalletTopUp> {
    const { data } = await HttpApiService.get<WalletTopUp>(`${WALLET_TOP_UPS_URL}/${id}`);
    return data;
  },

  /** What became of a create whose answer was lost: the top-up, or 404 not found / 409 in progress. */
  async findByKey(organizationId: string, idempotencyKey: string): Promise<WalletTopUp> {
    const { data } = await HttpApiService.get<WalletTopUp>(
      `${WALLET_TOP_UPS_URL}/by-key/${encodeURIComponent(idempotencyKey)}`,
      { params: { organization_id: organizationId } },
    );
    return data;
  },

  async create(payload: CreateWalletTopUpPayload, twoFactorCode: string): Promise<WalletTopUpCreated> {
    const { data } = await HttpApiService.post<WalletTopUpCreated>(WALLET_TOP_UPS_URL, payload, {
      headers: { "x-two-factor-code": twoFactorCode },
    });
    return data;
  },

  async receipt(topUp: Pick<WalletTopUp, "id" | "reference">): Promise<WalletTopUpReceiptFile> {
    try {
      const response = await HttpApiService.get<Blob>(`${WALLET_TOP_UPS_URL}/${topUp.id}/receipt`, {
        responseType: "blob",
      });
      const header = (response.headers as Record<string, string | undefined>)["content-disposition"];
      return {
        blob: response.data,
        filename: parseContentDispositionFilename(header) ?? `comprobante-recarga-${topUp.reference}.pdf`,
      };
    } catch (error: unknown) {
      throw await normalizeBlobError(error);
    }
  },
};
